import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Collapse,
  withStyles
} from '@material-ui/core';
import {
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Refresh as RetryIcon
} from '@material-ui/icons';
// Removed notistack dependency - enqueueSnackbar passed as prop

const styles = (theme) => ({
  exportButton: {
    marginLeft: theme.spacing(1),
  },
  loadingIcon: {
    marginRight: theme.spacing(1),
  },
  menuItem: {
    minWidth: 200,
  },
  exportIcon: {
    minWidth: 36,
  },
  progressDialog: {
    minWidth: 400,
  },
  progressContent: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  progressBar: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statusIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  errorDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    maxHeight: 200,
    overflow: 'auto',
  },
  progressStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  estimatedTime: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
});

const QuickNotesExportButton = ({
  data,
  chartType,
  selectedMetric,
  selectedStack,
  selectedMonth,
  selectedPeriod,
  dateRange,
  onExport,
  disabled = false,
  variant = 'outlined',
  size = 'small',
  classes,
  enqueueSnackbar
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('idle'); // idle, preparing, exporting, success, error
  const [exportError, setExportError] = useState(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const [exportStats, setExportStats] = useState({ processed: 0, total: 0 });
  const [retryCount, setRetryCount] = useState(0);
  const [currentExportType, setCurrentExportType] = useState(null);
  
  const abortControllerRef = useRef(null);
  const exportStartTimeRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Cleanup function for progress tracking
  const cleanupProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanupProgressTracking;
  }, [cleanupProgressTracking]);

  // Reset export state
  const resetExportState = useCallback(() => {
    setIsExporting(false);
    setShowProgressDialog(false);
    setExportProgress(0);
    setExportStatus('idle');
    setExportError(null);
    setShowErrorDetails(false);
    setEstimatedTimeRemaining(null);
    setExportStats({ processed: 0, total: 0 });
    setCurrentExportType(null);
    cleanupProgressTracking();
  }, [cleanupProgressTracking]);

  // Estimate progress based on data size and time elapsed
  const updateProgressEstimate = useCallback((processed, total) => {
    const elapsed = Date.now() - exportStartTimeRef.current;
    const progress = total > 0 ? (processed / total) * 100 : 0;
    
    setExportProgress(Math.min(progress, 95)); // Cap at 95% until completion
    setExportStats({ processed, total });
    
    if (processed > 0 && elapsed > 1000) { // After 1 second
      const avgTimePerItem = elapsed / processed;
      const remaining = total - processed;
      const estimatedMs = remaining * avgTimePerItem;
      
      if (estimatedMs > 1000) {
        const estimatedSec = Math.ceil(estimatedMs / 1000);
        if (estimatedSec < 60) {
          setEstimatedTimeRemaining(`~${estimatedSec} seconds remaining`);
        } else {
          const estimatedMin = Math.ceil(estimatedSec / 60);
          setEstimatedTimeRemaining(`~${estimatedMin} minute${estimatedMin > 1 ? 's' : ''} remaining`);
        }
      } else {
        setEstimatedTimeRemaining('Almost done...');
      }
    }
  }, []);

  // Enhanced error handling
  const handleExportError = useCallback((error, context = {}) => {
    console.error('Export error:', error, context);
    
    let errorMessage = 'An unexpected error occurred during export';
    let errorDetails = error.stack || error.message || 'Unknown error';
    
    if (error.name === 'AbortError') {
      errorMessage = 'Export was cancelled';
      errorDetails = 'The export operation was cancelled by the user';
    } else if (error.message?.includes('Network')) {
      errorMessage = 'Network error during export';
      errorDetails = 'Please check your internet connection and try again';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'Export timed out';
      errorDetails = 'The export took too long to complete. Try reducing the date range or contact support';
    } else if (error.message?.includes('size')) {
      errorMessage = 'Dataset too large for export';
      errorDetails = 'Please reduce the date range or apply additional filters';
    } else if (error.response) {
      errorMessage = `Server error (${error.response.status})`;
      errorDetails = error.response.data?.error || error.response.statusText || 'Server returned an error';
    }
    
    setExportError({
      message: errorMessage,
      details: errorDetails,
      canRetry: !error.name?.includes('Abort') && retryCount < 3,
      context
    });
    setExportStatus('error');
    
    enqueueSnackbar(errorMessage, { 
      variant: 'error',
      persist: true,
      action: (key) => (
        <IconButton size="small" onClick={() => closeSnackbar(key)} color="inherit">
          <CancelIcon />
        </IconButton>
      )
    });
  }, [retryCount, enqueueSnackbar]);

  // Generate export filename based on context
  const generateFilename = (exportType) => {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const metric = selectedMetric === 'count' ? 'Count' : 'TimeSpent';
    const stack = selectedStack.replace('_', '').replace(/^\w/, c => c.toUpperCase());
    
    let filename = `QuickNotes_${chartType}_${metric}_${stack}_${timestamp}`;
    
    if (selectedMonth) {
      const monthLabel = new Date(selectedMonth + '-01').toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit'
      }).replace('/', '-');
      filename += `_${monthLabel}`;
    }
    
    filename += '_Report';
    if (exportType === 'pdf') {
      filename += '.pdf';
    } else {
      filename += '.csv';
    }
    
    return filename;
  };

  // Transform data for CSV export
  const transformDataForExport = (exportType) => {
    if (!data || data.length === 0) {
      return { headers: [], rows: [] };
    }

    // Export data transformation - includes all available data points
    const headers = [
      'Period',
      'Group',
      'Count',
      'Time Spent (minutes)', 
      'Percentage of Period',
      'Chart Type',
      'Stack Dimension',
      'Metric Type'
    ];
    const rows = [];
    
    data.forEach(item => {
      const period = item.monthLabel || item.ownerName || item.categoryName || item.month;
      const totalCount = item.totals?.count || 0;
      const totalTime = item.totals?.timeSpentMinutes || 0;
      
      if (item.buckets && item.buckets.length > 0) {
        item.buckets.forEach(bucket => {
          const countPercentage = totalCount > 0 ? ((bucket.count / totalCount) * 100).toFixed(1) : '0.0';
          const timePercentage = totalTime > 0 ? ((bucket.timeSpentMinutes / totalTime) * 100).toFixed(1) : '0.0';
          
          rows.push([
            period,
            bucket.key,
            bucket.count,
            bucket.timeSpentMinutes,
            selectedMetric === 'count' ? `${countPercentage}%` : `${timePercentage}%`,
            chartType,
            selectedStack,
            selectedMetric
          ]);
        });
      } else {
        rows.push([
          period,
          selectedStack,
          item.count || 0,
          item.timeSpentMinutes || 0,
          '100.0%',
          chartType,
          selectedStack,
          selectedMetric
        ]);
      }
    });
    
    return { headers, rows };
  };

  // Convert data to CSV string
  const convertToCSV = (headers, rows) => {
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => {
          // Handle cells that contain commas or quotes
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      )
    ].join('\n');
    
    return csvContent;
  };

  // Enhanced export handler with progress tracking
  const handleExport = async (exportType) => {
    console.log('QuickNotesExportButton: handleExport called with:', { exportType, hasOnExport: !!onExport, dataLength: data?.length });
    
    // For server-side exports (when onExport is provided), we don't need client-side data
    if (!onExport && (!data || data.length === 0)) {
      console.log('QuickNotesExportButton: No data or onExport handler, showing warning');
      enqueueSnackbar('No data available to export', { variant: 'warning' });
      return;
    }

    // Reset state and prepare for export
    resetExportState();
    setIsExporting(true);
    setCurrentExportType(exportType);
    setAnchorEl(null);
    setExportStatus('preparing');
    setShowProgressDialog(true);
    exportStartTimeRef.current = Date.now();

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      setExportStatus('exporting');
      
      if (onExport) {
        // Use custom server-side export handler
        const exportParams = {
          data,
          exportType,
          chartType,
          selectedMetric,
          selectedStack,
          selectedMonth,
          selectedPeriod,
          dateRange,
          filename: generateFilename(exportType),
          signal: abortControllerRef.current.signal
        };

        // For server-side exports, we don't have exact row counts, so use a reasonable estimate
        const estimatedRows = 100; // Rough estimate for CSV export
        
        setExportStats({ processed: 0, total: estimatedRows });

        // Start progress simulation for server exports
        progressIntervalRef.current = setInterval(() => {
          setExportProgress(prev => {
            const increment = Math.random() * 10;
            const newProgress = Math.min(prev + increment, 85);
            return newProgress;
          });
        }, 500);

        console.log('QuickNotesExportButton: Calling onExport with:', exportParams);
        await onExport(exportParams);
        console.log('QuickNotesExportButton: onExport completed successfully');
        
      } else {
        // Client-side CSV generation with progress tracking
        const { headers, rows } = transformDataForExport(exportType);
        setExportStats({ processed: 0, total: rows.length });
        
        // Simulate progress for CSV generation
        const chunkSize = Math.max(1, Math.floor(rows.length / 20));
        let processedRows = 0;
        
        const csvChunks = [headers.join(',')];
        
        for (let i = 0; i < rows.length; i += chunkSize) {
          if (abortControllerRef.current?.signal.aborted) {
            throw new Error('Export cancelled');
          }
          
          const chunk = rows.slice(i, i + chunkSize);
          const chunkCsv = chunk.map(row => 
            row.map(cell => {
              const cellStr = String(cell || '');
              if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
              }
              return cellStr;
            }).join(',')
          );
          
          csvChunks.push(...chunkCsv);
          processedRows = Math.min(i + chunkSize, rows.length);
          updateProgressEstimate(processedRows, rows.length);
          
          // Allow UI to update
          await new Promise(resolve => setTimeout(resolve, 10));
        }
        
        const csvContent = csvChunks.join('\n');
        const filename = generateFilename(exportType);
        
        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }
      
      // Success state
      setExportProgress(100);
      setExportStatus('success');
      setEstimatedTimeRemaining(null);
      
      enqueueSnackbar(`Export completed: ${generateFilename(exportType)}`, { 
        variant: 'success' 
      });
      
      // Auto-close dialog after success
      setTimeout(() => {
        resetExportState();
      }, 2000);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        handleExportError(error, { exportType, chartType, selectedMetric });
      }
    } finally {
      cleanupProgressTracking();
      setIsExporting(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Retry export with exponential backoff
  const handleRetry = useCallback(() => {
    if (retryCount >= 3) {
      enqueueSnackbar('Maximum retry attempts reached', { variant: 'error' });
      return;
    }
    
    setRetryCount(prev => prev + 1);
    setExportError(null);
    setShowErrorDetails(false);
    
    // Add delay for exponential backoff
    const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
    setTimeout(() => {
      if (currentExportType) {
        handleExport(currentExportType);
      }
    }, delay);
  }, [retryCount, currentExportType, handleExport, enqueueSnackbar]);

  // Cancel export operation
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    resetExportState();
    enqueueSnackbar('Export cancelled', { variant: 'info' });
  }, [resetExportState, enqueueSnackbar]);

  // Close progress dialog
  const handleCloseDialog = useCallback(() => {
    if (exportStatus === 'exporting') {
      handleCancel();
    } else {
      resetExportState();
    }
  }, [exportStatus, handleCancel, resetExportState]);

  const isMenuOpen = Boolean(anchorEl);
  // For server-side exports, we don't need client-side data
  // Check if onExport function is provided (indicates server-side export capability)
  const hasData = onExport ? true : (data && data.length > 0);

  return (
    <>
      <Tooltip 
        title={hasData ? 'Export chart data to CSV' : 'No data to export'}
        placement="top"
      >
        <span>
          <Button
            variant={variant}
            size={size}
            onClick={handleMenuOpen}
            disabled={disabled || !hasData || isExporting}
            className={classes.exportButton}
            startIcon={
              isExporting ? (
                <CircularProgress size={16} className={classes.loadingIcon} />
              ) : (
                <ExportIcon />
              )
            }
            endIcon={!isExporting && <ExpandMoreIcon />}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem 
          onClick={(e) => {
            console.log('Export Button: PDF clicked');
            e.preventDefault();
            handleExport('pdf');
          }}
          className={classes.menuItem}
        >
          <ListItemIcon className={classes.exportIcon}>
            <PdfIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Visual Report (PDF)"
            secondary="Charts and tables in PDF format"
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            console.log('Export Button: CSV clicked');
            e.preventDefault();
            handleExport('csv');
          }}
          className={classes.menuItem}
        >
          <ListItemIcon className={classes.exportIcon}>
            <ExcelIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Data Export (CSV)"
            secondary="Charts data in CSV format"
          />
        </MenuItem>
      </Menu>

      {/* Progress Dialog */}
      <Dialog
        open={showProgressDialog}
        onClose={exportStatus !== 'exporting' ? handleCloseDialog : undefined}
        disableBackdropClick={exportStatus === 'exporting'}
        disableEscapeKeyDown={exportStatus === 'exporting'}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.progressDialog }}
      >
        <DialogTitle>
          {exportStatus === 'preparing' && 'Preparing Export...'}
          {exportStatus === 'exporting' && 'Exporting Data...'}
          {exportStatus === 'success' && 'Export Complete'}
          {exportStatus === 'error' && 'Export Failed'}
        </DialogTitle>
        
        <DialogContent className={classes.progressContent}>
          {/* Status Icon */}
          {exportStatus === 'preparing' && (
            <CircularProgress className={classes.statusIcon} />
          )}
          {exportStatus === 'exporting' && (
            <CircularProgress className={classes.statusIcon} />
          )}
          {exportStatus === 'success' && (
            <SuccessIcon className={`${classes.statusIcon} ${classes.successIcon}`} />
          )}
          {exportStatus === 'error' && (
            <ErrorIcon className={`${classes.statusIcon} ${classes.errorIcon}`} />
          )}

          {/* Progress Bar */}
          {(exportStatus === 'preparing' || exportStatus === 'exporting') && (
            <Box className={classes.progressBar}>
              <LinearProgress 
                variant={exportStatus === 'preparing' ? 'indeterminate' : 'determinate'} 
                value={exportProgress}
                style={{ height: 8, borderRadius: 4 }}
              />
              {exportStatus === 'exporting' && (
                <Box className={classes.progressStats}>
                  <Typography variant="body2">
                    {exportStats.processed} of {exportStats.total} items
                  </Typography>
                  <Typography variant="body2">
                    {Math.round(exportProgress)}%
                  </Typography>
                </Box>
              )}
              {estimatedTimeRemaining && (
                <Typography variant="body2" className={classes.estimatedTime}>
                  {estimatedTimeRemaining}
                </Typography>
              )}
            </Box>
          )}

          {/* Status Messages */}
          {exportStatus === 'preparing' && (
            <Typography variant="body1">
              Preparing your export...
            </Typography>
          )}
          {exportStatus === 'exporting' && (
            <Typography variant="body1">
              Generating {currentExportType} export for {chartType} chart...
            </Typography>
          )}
          {exportStatus === 'success' && (
            <Typography variant="body1" color="primary">
              Your export has been downloaded successfully!
            </Typography>
          )}
          {exportStatus === 'error' && exportError && (
            <Box>
              <Typography variant="body1" color="error">
                {exportError.message}
              </Typography>
              {exportError.canRetry && (
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  You can try the export again or contact support if the problem persists.
                </Typography>
              )}
              <Button
                size="small"
                onClick={() => setShowErrorDetails(!showErrorDetails)}
                style={{ marginTop: 8 }}
              >
                {showErrorDetails ? 'Hide' : 'Show'} Details
              </Button>
              <Collapse in={showErrorDetails}>
                <Box className={classes.errorDetails}>
                  {exportError.details}
                </Box>
              </Collapse>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {exportStatus === 'exporting' && (
            <Button onClick={handleCancel} color="secondary">
              Cancel
            </Button>
          )}
          {exportStatus === 'error' && exportError?.canRetry && (
            <Button 
              onClick={handleRetry} 
              color="primary" 
              startIcon={<RetryIcon />}
              disabled={retryCount >= 3}
            >
              Retry {retryCount > 0 && `(${retryCount}/3)`}
            </Button>
          )}
          {(exportStatus === 'success' || exportStatus === 'error') && (
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

QuickNotesExportButton.propTypes = {
  data: PropTypes.array.isRequired,
  chartType: PropTypes.oneOf(['ytd', 'owner', 'category']).isRequired,
  selectedMetric: PropTypes.oneOf(['count', 'time_spent']).isRequired,
  selectedStack: PropTypes.oneOf(['activity_type', 'planned', 'category']).isRequired,
  selectedMonth: PropTypes.string,
  selectedPeriod: PropTypes.string,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  onExport: PropTypes.func,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  classes: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
};

export default withStyles(styles)(QuickNotesExportButton);
